/**
 * Bounded LRU image cache + prioritised, concurrency-limited preloader
 * for radar tile playback.
 */

// ============================================================================
// LRU Image Cache
// ============================================================================

export class LRUImageCache {
  private map = new Map<string, HTMLImageElement>();
  private readonly maxSize: number;

  constructor(maxSize = 150) {
    this.maxSize = maxSize;
  }

  has(key: string): boolean {
    return this.map.has(key);
  }

  get(key: string): HTMLImageElement | undefined {
    const img = this.map.get(key);
    if (img) {
      // Move to end (most recently used)
      this.map.delete(key);
      this.map.set(key, img);
    }
    return img;
  }

  set(key: string, img: HTMLImageElement): void {
    if (this.map.has(key)) {
      this.map.delete(key);
    }
    this.map.set(key, img);
    this.evict();
  }

  clear(): void {
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }

  private evict(): void {
    while (this.map.size > this.maxSize) {
      const oldest = this.map.keys().next().value;
      if (oldest !== undefined) {
        this.map.delete(oldest);
      }
    }
  }
}

// ============================================================================
// Prioritised Preloader
// ============================================================================

interface PreloadTask {
  url: string;
  priority: number; // lower = higher priority
}

/**
 * Concurrency-limited preloader that always caches completed loads,
 * even if the queue was reprioritized in the meantime.
 *
 * `schedule()` replaces the pending queue but does NOT invalidate
 * in-flight loads -- those images are still useful to the cache.
 * Only `cancel()` (called when the tile list itself changes) sets
 * the `aborted` flag, which prevents further queue draining until
 * `resume()` is called.
 */
export class RadarPreloader {
  private cache: LRUImageCache;
  private queue: PreloadTask[] = [];
  /** URLs currently being fetched -- prevents duplicate concurrent loads. */
  private pending = new Set<string>();
  private activeCount = 0;
  private readonly maxConcurrent: number;
  private aborted = false;

  constructor(cache: LRUImageCache, maxConcurrent = 4) {
    this.cache = cache;
    this.maxConcurrent = maxConcurrent;
  }

  /**
   * Replace the pending queue with new priorities and drain.
   * Does NOT cancel or discard in-flight loads -- they continue,
   * cache their results, and free a concurrency slot for the new queue.
   */
  schedule(tasks: PreloadTask[]): void {
    this.queue = tasks
      .filter((t) => !this.cache.has(t.url) && !this.pending.has(t.url))
      .sort((a, b) => a.priority - b.priority);
    this.drain();
  }

  /**
   * Hard cancel -- empty the queue and prevent further draining.
   * In-flight loads still complete and cache their results (the image
   * bytes are already in transit), but they won't pull new work.
   */
  cancel(): void {
    this.aborted = true;
    this.queue = [];
  }

  /** Resume after a cancel (e.g. new tiles arrived). */
  resume(): void {
    this.aborted = false;
  }

  destroy(): void {
    this.cancel();
  }

  // --------------------------------------------------------------------------

  private drain(): void {
    if (this.aborted) return;
    while (this.activeCount < this.maxConcurrent && this.queue.length > 0) {
      const task = this.queue.shift()!;
      if (this.cache.has(task.url) || this.pending.has(task.url)) continue;
      this.load(task.url);
    }
  }

  private load(url: string): void {
    this.pending.add(url);
    this.activeCount++;
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const done = () => {
      this.pending.delete(url);
      this.activeCount--;
      // Drain the *latest* queue (which may have been replaced by a
      // newer schedule() call while this image was loading).
      if (!this.aborted) {
        this.drain();
      }
    };

    img.onload = () => {
      // Always cache -- the image data is useful regardless of whether
      // the queue was reprioritized since this load started.
      this.cache.set(url, img);
      if (typeof img.decode === 'function') {
        img.decode().catch(() => {}).finally(done);
      } else {
        done();
      }
    };

    img.onerror = () => {
      done();
    };

    img.src = url;
  }
}

// ============================================================================
// Priority builder
// ============================================================================

interface RadarTileMinimal {
  url: string;
}

/**
 * Build a prioritised preload list:
 *   0  – currentIndex
 *   1-5 – next 5 frames
 *   6  – loop-start (index 0)
 *   7  – loop-end (last index)
 *   8+ – remaining frames (distance from current)
 */
export function buildPreloadPriorities(
  tiles: RadarTileMinimal[],
  currentIndex: number,
): PreloadTask[] {
  if (tiles.length === 0) return [];
  const tasks: PreloadTask[] = [];
  const seen = new Set<number>();

  const add = (idx: number, priority: number) => {
    if (idx < 0 || idx >= tiles.length || seen.has(idx)) return;
    seen.add(idx);
    tasks.push({ url: tiles[idx].url, priority });
  };

  // Current frame
  add(currentIndex, 0);

  // Next 5 frames
  for (let i = 1; i <= 5; i++) {
    add(currentIndex + i, i);
  }

  // Loop boundaries
  add(0, 6);
  add(tiles.length - 1, 7);

  // Remaining frames ordered by distance from current
  for (let dist = 1; dist < tiles.length; dist++) {
    add(currentIndex - dist, 8 + dist);
    add(currentIndex + dist, 8 + dist);
  }

  return tasks;
}
